/**
 * Labels and prompts of the built-in tools in English and Turkish.
 *
 * Tools report message keys (`tool.line`, `tool.line.start`) instead of text, so any
 * UI — the React editor or a plain DOM page — shows them in its language with
 * [`toolText`]. Plugin tools may use plain text; unknown keys come back unchanged.
 */
export const TOOL_MESSAGES_EN = {
    'tool.select': 'Select',
    'tool.pan': 'Pan',
    'tool.line': 'Line',
    'tool.polyline': 'Polyline',
    'tool.rect': 'Rectangle',
    'tool.circle': 'Circle',
    'tool.arc': 'Arc',
    'tool.path': 'Path',
    'tool.text': 'Text',
    'tool.dimension': 'Dimension',
    'tool.move': 'Move',
    'tool.rotate': 'Rotate',
    'tool.scale': 'Scale',
    'tool.split': 'Split',
    'tool.trim': 'Trim',
    'tool.extend': 'Extend',
    'tool.select.prompt': 'Click to select, drag to select an area (right-to-left touches), drag objects to move.',
    'tool.select.dragging': 'Release to place.',
    'tool.select.dragRejected': 'This position breaks a rule; the nearest allowed position is shown.',
    'tool.line.start': 'Line: click the start point.',
    'tool.line.end': 'Line: click the end point or type a length (Shift = horizontal/vertical).',
    'tool.polyline.start': 'Polyline: click the first point.',
    'tool.polyline.next': 'Polyline: click the next point; Enter or double-click finishes, click the first point to close.',
    'tool.rect.start': 'Rectangle: click or drag from a corner.',
    'tool.rect.end': 'Rectangle: click the opposite corner or type width,height.',
    'tool.circle.center': 'Circle: click the center.',
    'tool.circle.radius': 'Circle: click a point on the circle or type the radius.',
    'tool.arc.start': 'Arc: click the start point.',
    'tool.arc.mid': 'Arc: click a point on the arc.',
    'tool.arc.end': 'Arc: click the end point.',
    'tool.path.start': 'Path: click to start; drag to pull a curve handle.',
    'tool.path.next': 'Path: click or drag the next point; Enter finishes, click the start to close.',
    'tool.text.position': 'Text: click where the text starts.',
    'tool.text.type': 'Text: type, then Enter (Shift+Enter for a new line, Escape cancels).',
    'tool.dimension.first': 'Dimension: click the first point (snap to an object to keep it associated).',
    'tool.dimension.second': 'Dimension: click the second point.',
    'tool.dimension.place': 'Dimension: click where the dimension line goes.',
    'tool.move.select': 'Move: select objects first (click one).',
    'tool.move.base': 'Move: click the base point.',
    'tool.move.target': 'Move: click the destination or type dx,dy.',
    'tool.rotate.select': 'Rotate: select objects first (click one).',
    'tool.rotate.center': 'Rotate: click the center or type an angle after it.',
    'tool.rotate.reference': 'Rotate: click the reference direction or type an angle in degrees.',
    'tool.rotate.target': 'Rotate: click the new direction.',
    'tool.scale.select': 'Scale: select objects first (click one).',
    'tool.scale.base': 'Scale: click the base point.',
    'tool.scale.reference': 'Scale: click a reference point or type a factor.',
    'tool.scale.target': 'Scale: click the new position of the reference point.',
    'tool.split.pick': 'Split: click a curve where it should be cut.',
    'tool.trim.pick': 'Trim: click the piece to remove.',
    'tool.extend.pick': 'Extend: click near the end to extend.',
};
export const TOOL_MESSAGES_TR = {
    'tool.select': 'Seç',
    'tool.pan': 'Kaydır',
    'tool.line': 'Çizgi',
    'tool.polyline': 'Çoklu çizgi',
    'tool.rect': 'Dikdörtgen',
    'tool.circle': 'Çember',
    'tool.arc': 'Yay',
    'tool.path': 'Yol',
    'tool.text': 'Metin',
    'tool.dimension': 'Ölçü',
    'tool.move': 'Taşı',
    'tool.rotate': 'Döndür',
    'tool.scale': 'Ölçekle',
    'tool.split': 'Böl',
    'tool.trim': 'Kırp',
    'tool.extend': 'Uzat',
    'tool.select.prompt': 'Seçmek için tıklayın; alan seçmek için sürükleyin (sağdan sola dokunanları seçer); nesneleri sürükleyerek taşıyın.',
    'tool.select.dragging': 'Yerleştirmek için bırakın.',
    'tool.select.dragRejected': 'Bu konum bir kuralı bozuyor; izin verilen en yakın konum gösteriliyor.',
    'tool.line.start': 'Çizgi: başlangıç noktasını tıklayın.',
    'tool.line.end': 'Çizgi: bitiş noktasını tıklayın veya uzunluk yazın (Shift = yatay/dikey).',
    'tool.polyline.start': 'Çoklu çizgi: ilk noktayı tıklayın.',
    'tool.polyline.next': 'Çoklu çizgi: sonraki noktayı tıklayın; Enter veya çift tıklama bitirir, kapatmak için ilk noktayı tıklayın.',
    'tool.rect.start': 'Dikdörtgen: bir köşeden tıklayın veya sürükleyin.',
    'tool.rect.end': 'Dikdörtgen: karşı köşeyi tıklayın veya genişlik,yükseklik yazın.',
    'tool.circle.center': 'Çember: merkezi tıklayın.',
    'tool.circle.radius': 'Çember: çember üzerinde bir nokta tıklayın veya yarıçap yazın.',
    'tool.arc.start': 'Yay: başlangıç noktasını tıklayın.',
    'tool.arc.mid': 'Yay: yay üzerinde bir nokta tıklayın.',
    'tool.arc.end': 'Yay: bitiş noktasını tıklayın.',
    'tool.path.start': 'Yol: başlamak için tıklayın; eğri tutamağı için sürükleyin.',
    'tool.path.next': 'Yol: sonraki noktayı tıklayın veya sürükleyin; Enter bitirir, kapatmak için başlangıcı tıklayın.',
    'tool.text.position': 'Metin: metnin başlayacağı yeri tıklayın.',
    'tool.text.type': 'Metin: yazın, sonra Enter (yeni satır için Shift+Enter, iptal için Escape).',
    'tool.dimension.first': 'Ölçü: ilk noktayı tıklayın (bağlı kalması için bir nesneye yakalayın).',
    'tool.dimension.second': 'Ölçü: ikinci noktayı tıklayın.',
    'tool.dimension.place': 'Ölçü: ölçü çizgisinin yerini tıklayın.',
    'tool.move.select': 'Taşı: önce nesne seçin (birine tıklayın).',
    'tool.move.base': 'Taşı: temel noktayı tıklayın.',
    'tool.move.target': 'Taşı: hedefi tıklayın veya dx,dy yazın.',
    'tool.rotate.select': 'Döndür: önce nesne seçin (birine tıklayın).',
    'tool.rotate.center': 'Döndür: merkezi tıklayın.',
    'tool.rotate.reference': 'Döndür: referans yönünü tıklayın veya derece cinsinden açı yazın.',
    'tool.rotate.target': 'Döndür: yeni yönü tıklayın.',
    'tool.scale.select': 'Ölçekle: önce nesne seçin (birine tıklayın).',
    'tool.scale.base': 'Ölçekle: temel noktayı tıklayın.',
    'tool.scale.reference': 'Ölçekle: bir referans noktası tıklayın veya ölçek çarpanı yazın.',
    'tool.scale.target': 'Ölçekle: referans noktasının yeni yerini tıklayın.',
    'tool.split.pick': 'Böl: eğrinin kesileceği yeri tıklayın.',
    'tool.trim.pick': 'Kırp: silinecek parçayı tıklayın.',
    'tool.extend.pick': 'Uzat: uzatılacak ucun yakınını tıklayın.',
};
/** Text of a tool label or prompt key (`'tr…'` locales get Turkish, others English). */
export function toolText(key, locale = 'en') {
    const table = locale.toLowerCase().startsWith('tr') ? TOOL_MESSAGES_TR : TOOL_MESSAGES_EN;
    return table[key] ?? key;
}
//# sourceMappingURL=messages.js.map